import React, { Suspense, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import AIAssistantChat from './components/AIAssistantChat';
import Loader from '../Loader';
import useAiChatSession from './useAiChatSession';
import { clearAiAssistantPrompt, closeAiAssistant, setAiAssistantMode, setAiAssistantTokenLimitReached } from '../../sagas/aiAssistant/actions';
import { setSuggestionsToDisplay } from '../../sagas/aiPromptSuggestions/actions';
import { isAiAssistantVisiblePath, isAiAssistantBarPath } from '../../utils/menu';
import { ENVIRONMENT } from '../../config';

import './_aiAssistant.scss';

const AIAssistant = ({ allowDevAuth = false }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isSmallScreen = useMediaQuery('(max-width:576px)');
  const { open, mode, returnMode, pendingPrompt } = useSelector(state => state.aiAssistant);
  const { navbar, menu } = useSelector(state => state.layout);
  const { data: accountData } = useSelector(state => state.account);
  const { chatKey, handleAuthExpired, aiChatToken } = useAiChatSession({ active: open });
  const avatarUrl = accountData?.avatar_url;
  const accountUuid = accountData?.uuid;
  const firstName = accountData?.first_name || '';

  // Must be before any conditional returns (hooks rule)
  const hasMountedRef = useRef(false);
  const [isFloatingExpanded, setIsFloatingExpanded] = React.useState(false);

  const isVisiblePath = isAiAssistantVisiblePath(pathname);
  const isAiAssistantBarRoute = isAiAssistantBarPath(pathname);

  useEffect(() => {
    if (!isVisiblePath && open) {
      dispatch(closeAiAssistant());
    }
  }, [dispatch, isVisiblePath, open]);

  // The floating window belongs to the Ai Bar, so fall back to the sidebar on routes without it.
  useEffect(() => {
    if (open && isVisiblePath && !isAiAssistantBarRoute && mode === 'floating') {
      dispatch(setAiAssistantMode('sidebar'));
    }
  }, [dispatch, isAiAssistantBarRoute, isVisiblePath, mode, open]);

  useEffect(() => {
    if (!open || mode !== 'floating') {
      setIsFloatingExpanded(false);
    }
  }, [mode, open]);

  useEffect(() => {
    if (!open || mode !== 'floating') {
      return undefined;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        dispatch(setSuggestionsToDisplay(null));
        dispatch(closeAiAssistant());
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, mode, open]);

  if (!navbar && !menu) {
    return null;
  }

  if (!isVisiblePath) {
    return null;
  }

  // Don't mount until token is ready in non-dev mode to avoid connecting with empty query.
  if (!(allowDevAuth && ENVIRONMENT === 'development') && !aiChatToken) {
    return null;
  }

  if (open) hasMountedRef.current = true;
  if (!hasMountedRef.current) return null;

  const handleClose = () => {
    dispatch(setSuggestionsToDisplay(null));
    dispatch(closeAiAssistant());
  };

  const handleModeChange = () => {
    if (mode === 'floating') {
      setIsFloatingExpanded(expanded => !expanded);
      return;
    }

    if (mode !== 'fullscreen') {
      dispatch(setAiAssistantMode('fullscreen'));
      return;
    }

    const canReturnToFloating = returnMode === 'floating' && isAiAssistantBarRoute;
    dispatch(setAiAssistantMode(canReturnToFloating ? 'floating' : 'sidebar'));
  };

  const chatProps = {
    avatarUrl,
    accountUuid,
    firstName,
    allowDevAuth,
    mode,
    onClose: handleClose,
    onModeChange: handleModeChange,
    onAuthExpired: handleAuthExpired,
    isSmallScreen,
    isOpen: open,
    isExpanded: mode === 'floating' && isFloatingExpanded,
    // Prompt from openAiAssistantWithPrompt (e.g. Creator page "Review Suggestion")
    initialPrompt: pendingPrompt,
    onInitialPromptSent: () => dispatch(clearAiAssistantPrompt()),
    onTokenLimitReached: () => dispatch(setAiAssistantTokenLimitReached())
  };

  const isSidebar = mode === 'sidebar';
  const containerClassName = {
    sidebar: 'aiAssistant-sidebar',
    fullscreen: 'aiAssistant-fullscreen',
    floating: isFloatingExpanded ? 'aiAssistant-floating aiAssistant-floating--expanded' : 'aiAssistant-floating'
  }[mode] || 'aiAssistant-sidebar';

  return (
    <>
      {!isSidebar && open && (
        <div className="aiAssistant-backdrop" onClick={handleClose} />
      )}
      <div
        className={containerClassName}
        style={{ display: open ? undefined : 'none' }}
      >
        {/* The chat suspends while it gets the token to connect with and loads history; Layout renders this
            outside the routes' Suspense, so without its own boundary it would suspend the whole app. */}
        <Suspense fallback={<Loader />}>
          <AIAssistantChat key={chatKey} {...chatProps} />
        </Suspense>
      </div>
    </>
  );
};

export default AIAssistant;

AIAssistant.propTypes = {
  allowDevAuth: PropTypes.bool
};